import * as React from 'react';
import { Appearance } from 'react-native';
import { atom, DefaultValue, useRecoilState, useRecoilValue } from 'recoil';
import { shallowEqualObjects } from 'shallow-equal';

import { localStorage } from '@gitbook/util-local-storage';
import { useSafeLayoutEffect, useStable } from '@gitbook/util-react-hooks';

import {
    deKeyVariantsValue,
    getDefaultVariantsValue,
    type KeyedVariantsValue,
    keyVariantsValue,
    Variants,
    type VariantsValue,
} from './variants';

interface ThemeConfigurationState {
    system: KeyedVariantsValue;
    app: KeyedVariantsValue | undefined;
}

const defaultConfigurationState: ThemeConfigurationState = {
    system: keyVariantsValue({
        ...getDefaultVariantsValue(),
        color: Appearance.getColorScheme() || Variants.color.default,
    }),
    app: undefined,
};

/**
 * Global atom to store the current theme and the one defined in the system.
 * Using a recoil state instead of a context to ensure changing the app theme applies to all elements.
 */
const configurationThemeAtom = atom<ThemeConfigurationState>({
    key: 'uikitConfigurationThemeAtom',
    default: defaultConfigurationState,
    effects: [
        // Read initial app theme from localStorage
        (effect) => {
            const storedTheme = localStorage.get<string>('@recoil/userThemeAtom');
            if (
                storedTheme &&
                typeof storedTheme === 'string' &&
                (storedTheme.includes('dark') || storedTheme.includes('light'))
            ) {
                const themeValue = storedTheme.includes('dark') ? 'dark' : 'light';
                effect.setSelf((prevValue) => {
                    const currentValue =
                        prevValue instanceof DefaultValue ? defaultConfigurationState : prevValue;
                    return {
                        ...currentValue,
                        app: keyVariantsValue({
                            ...getDefaultVariantsValue(),
                            color: themeValue,
                        }),
                    };
                });
            }
        },
        (effect) => {
            const callback = ({ colorScheme }: Appearance.AppearancePreferences) => {
                effect.setSelf((prevValue) => ({
                    app: prevValue instanceof DefaultValue ? undefined : prevValue.app,
                    system: keyVariantsValue({
                        ...getDefaultVariantsValue(),
                        color: colorScheme || Variants.color.default,
                    }),
                }));
            };

            const emitterSubscription = Appearance.addChangeListener(callback);
            return () => {
                emitterSubscription.remove();
            };
        },
    ],
});

/**
 * Read-only access to the theme configuration.
 * We use a context because reading a recoil atom is more expensive.
 */
export const ThemeConfigurationContext =
    React.createContext<ThemeConfigurationState>(defaultConfigurationState);

/**
 * Provider for the theming configuration.
 */
export function ThemeConfigurationProvider(props: {
    children: React.ReactNode;
}): React.ReactElement {
    const { children } = props;
    const value = useRecoilValue(configurationThemeAtom);

    // Handle the change of the color scheme in the browser when we don't have an app theme
    const handlePrefersColorSchemeChange = React.useCallback(
        (e: MediaQueryList | MediaQueryListEvent) => {
            const colorScheme = e.matches ? 'dark' : 'light';

            // Apply the correct theme classes to the body element
            Object.entries(Variants).forEach(([key, { values }]) => {
                values.forEach((value) => {
                    const className = `theme-${key}-${value}`;
                    document.documentElement.classList.toggle(className, value === colorScheme);
                });
            });
        },
        []
    );

    // Indicate to the browser which color-scheme as been choosen in the app
    // to ensure browser UI elements are correctly rendered.
    React.useEffect(() => {
        if (value.app) {
            // @ts-ignore 'colorScheme' does exist in modern browsers
            document.documentElement.style.colorScheme = deKeyVariantsValue(value.app).color;
        } else {
            // Set the initial color scheme when we don't have an app theme
            // Necessary for login/signup page, if we don't set the initial color scheme, tailwind can't apply the correct classes.
            const darkThemeMq = window.matchMedia('(prefers-color-scheme: dark)');
            handlePrefersColorSchemeChange(darkThemeMq);

            // Use addListener for compatibility with older browsers
            darkThemeMq.addListener(handlePrefersColorSchemeChange);

            return () => {
                darkThemeMq.removeListener(handlePrefersColorSchemeChange);
            };
        }
    }, [value, handlePrefersColorSchemeChange]);

    return (
        <ThemeConfigurationContext.Provider value={value}>
            {children}
        </ThemeConfigurationContext.Provider>
    );
}

/**
 * Define the palette/theme for the UI
 */
export function ConfigurationTheme(props: {
    theme: Partial<VariantsValue>;
    children: React.ReactNode;
}): React.ReactElement {
    const { children } = props;
    const [state, setState] = useRecoilState(configurationThemeAtom);
    const theme = useStable(props.theme, shallowEqualObjects);

    React.useEffect(() => {
        Object.entries(Variants).forEach(([key, { values }]) => {
            values.forEach((value) => {
                const className = `theme-${key}-${value}`;
                document.documentElement.classList.toggle(className, theme[key] === value);
            });
        });
    }, [theme]);

    const keyedVariantsValue = React.useMemo(() => {
        return keyVariantsValue({
            ...(state.app ? deKeyVariantsValue(state.app) : getDefaultVariantsValue()),
            ...theme,
        });
    }, [state.app, theme]);

    /**
     * It's important that we `setState` in an effect only when the theme value has changed.
     * Because `ConfigurationTheme` can be used by multiple components, we don't want to end up
     * in an infinite loop where those components are forcing a re-renders due to different
     * values of `keyedVariantsValue` (their theme)
     */
    useSafeLayoutEffect(() => {
        setState((prevValue) => {
            if (prevValue.app !== keyedVariantsValue) {
                return { ...prevValue, app: keyedVariantsValue };
            }
            return prevValue;
        });
    }, [keyedVariantsValue, setState]);

    return <>{children}</>;
}
